/*
 * COMPTABILITE — plan comptable, exercices, journal, etats et declarations.
 *
 * Toutes sous `moduleGuard` : une entreprise dont le plan n'inclut pas la
 * comptabilite ne doit pas seulement voir des ecrans vides, elle ne doit pas
 * pouvoir les atteindre.
 */
import { Routes } from '@angular/router';
import { permissionGuard } from '../core/guards/permission.guard';
import { moduleGuard } from '../core/guards/module.guard';
import { roleCheckGuard } from '../core/guards/role-check.guard';
import { ONGLETS_CONFIG_FISCALE, SEGMENT_AUCUN_ACCES } from '../features/accounting/config-fiscale/config-fiscale-onglets';
import { ongletParDefautGuard } from '../features/accounting/config-fiscale/config-fiscale.garde';

const [ONGLET_CONFIGURATION, ONGLET_REGLES, ONGLET_COMPTES] = ONGLETS_CONFIG_FISCALE;

export const routesComptabilite: Routes = [
  {
    path: 'accounting/chart-of-accounts',
    loadComponent: () => import('../features/accounting/chart-of-accounts/chart-of-accounts.component').then(m => m.ChartOfAccountsComponent),
    canActivate: [roleCheckGuard, moduleGuard, permissionGuard],
    data: { requiredPermission: { entity: 'CHART_OF_ACCOUNTS', action: 'READ' }, requiredModule: 'accounting' }
  },
  {
    path: 'accounting/fiscal-years',
    loadComponent: () => import('../features/accounting/fiscal-years/fiscal-years.component').then(m => m.FiscalYearsComponent),
    canActivate: [roleCheckGuard, moduleGuard, permissionGuard],
    data: { requiredPermission: { entity: 'FISCAL_YEAR', action: 'READ' }, requiredModule: 'accounting' }
  },
  {
    /*
     * CONFIGURATION COMPTABLE ET FISCALE — une coquille, trois onglets (routes enfants, donc liens profonds).
     * La coquille n'exige aucun droit : chaque enfant garde les gardes de l'ecran qu'il remplace, et la coquille
     * n'affiche que les onglets permis. L'enfant par defaut redirige vers le premier onglet accessible.
     */
    path: 'accounting/config',
    loadComponent: () => import('../features/accounting/config-fiscale/config-fiscale.component').then(m => m.ConfigFiscaleComponent),
    canActivate: [roleCheckGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () => import('../features/accounting/config/accounting-config.component').then(m => m.AccountingConfigComponent),
        canActivate: [ongletParDefautGuard, moduleGuard, permissionGuard],
        data: { ...ONGLET_CONFIGURATION.acces }
      },
      {
        path: ONGLET_REGLES.segment,
        loadComponent: () => import('../features/accounting/config-fiscale/regles-fiscales-onglet.component').then(m => m.ReglesFiscalesOngletComponent),
        canActivate: [moduleGuard, permissionGuard],
        data: { ...ONGLET_REGLES.acces }
      },
      {
        path: ONGLET_COMPTES.segment,
        loadComponent: () => import('../features/settings/tax-rules/tax-accounts-section/tax-accounts-section.component').then(m => m.TaxAccountsSectionComponent),
        canActivate: [moduleGuard, permissionGuard],
        data: { ...ONGLET_COMPTES.acces }
      },
      // Aucun onglet permis : la coquille le dit (message traduit). Volontairement sans garde.
      { path: SEGMENT_AUCUN_ACCES, children: [] },
    ]
  },
  {
    path: 'accounting/journal-entries',
    loadComponent: () => import('../features/accounting/reports/journal-entries/journal-entries.component').then(m => m.JournalEntriesComponent),
    canActivate: [roleCheckGuard, moduleGuard, permissionGuard],
    data: { requiredPermission: { entity: 'JOURNAL_ENTRY', action: 'READ' }, requiredModule: 'accounting' }
  },
  {
    path: 'accounting/general-ledger',
    loadComponent: () => import('../features/accounting/reports/general-ledger/general-ledger.component').then(m => m.GeneralLedgerComponent),
    canActivate: [roleCheckGuard, moduleGuard, permissionGuard],
    data: { requiredPermission: { entity: 'ACCOUNTING_REPORT', action: 'READ' }, requiredModule: 'accounting' }
  },
  {
    path: 'accounting/trial-balance',
    loadComponent: () => import('../features/accounting/reports/trial-balance/trial-balance.component').then(m => m.TrialBalanceComponent),
    canActivate: [roleCheckGuard, moduleGuard, permissionGuard],
    data: { requiredPermission: { entity: 'ACCOUNTING_REPORT', action: 'READ' }, requiredModule: 'accounting' }
  },
  {
    path: 'accounting/balance-sheet',
    loadComponent: () => import('../features/accounting/reports/balance-sheet/balance-sheet.component').then(m => m.BalanceSheetComponent),
    canActivate: [roleCheckGuard, moduleGuard, permissionGuard],
    data: { requiredPermission: { entity: 'ACCOUNTING_REPORT', action: 'READ' }, requiredModule: 'accounting' }
  },
  {
    path: 'accounting/income-statement',
    loadComponent: () => import('../features/accounting/reports/income-statement/income-statement.component').then(m => m.IncomeStatementComponent),
    canActivate: [roleCheckGuard, moduleGuard, permissionGuard],
    data: { requiredPermission: { entity: 'ACCOUNTING_REPORT', action: 'READ' }, requiredModule: 'accounting' }
  },
  {
    path: 'accounting/cash-flow',
    loadComponent: () => import('../features/accounting/reports/cash-flow/cash-flow.component').then(m => m.CashFlowComponent),
    canActivate: [roleCheckGuard, moduleGuard, permissionGuard],
    data: { requiredPermission: { entity: 'ACCOUNTING_REPORT', action: 'READ' }, requiredModule: 'accounting' }
  },
  {
    path: 'accounting/export',
    loadComponent: () => import('../features/accounting/reports/accounting-export/accounting-export.component').then(m => m.AccountingExportComponent),
    canActivate: [roleCheckGuard, moduleGuard, permissionGuard],
    data: { requiredPermission: { entity: 'ACCOUNTING_REPORT', action: 'READ' }, requiredModule: 'advancedAccounting' }
  },
  {
    path: 'accounting/tps-tvq-report',
    loadComponent: () => import('../features/accounting/reports/tps-tvq-report/tps-tvq-report.component').then(m => m.TpsTvqReportComponent),
    canActivate: [roleCheckGuard, moduleGuard, permissionGuard],
    data: { requiredPermission: { entity: 'ACCOUNTING_REPORT', action: 'READ' }, requiredModule: 'advancedAccounting' }
  },
  {
    path: 'accounting/cti-rti-report',
    loadComponent: () => import('../features/accounting/reports/cti-rti-report/cti-rti-report.component').then(m => m.CtiRtiReportComponent),
    canActivate: [roleCheckGuard, moduleGuard, permissionGuard],
    data: { requiredPermission: { entity: 'ACCOUNTING_REPORT', action: 'READ' }, requiredModule: 'advancedAccounting' }
  },
  {
    path: 'accounting/payroll',
    loadComponent: () => import('../features/accounting/payroll/payroll-dashboard.component').then(m => m.PayrollDashboardComponent),
    canActivate: [roleCheckGuard, moduleGuard, permissionGuard],
    data: { requiredPermission: { entity: 'PAYROLL', action: 'READ' }, requiredModule: 'hr' }
  },
];
